import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { RouteFromObjectType } from "../../common/utilities/RouteFromObjectType";
import restService from "../rest-service/RestService";
import { AcmObjectTypeEnum, IAcmConnectionObject, IAcmConnectionObjectUsage, IAcmDeviceProtocolObjectUsage, IAcmProtocolObject, IAcmProtocolObjectUsage, IAcmProtocolParams, IAcmTcpIpConnectionObject, IAcmTcpIpParams, IAcmTcpListenConnectionObject, IAcmTcpListenParams, ICIDRAddress, ICommDeviceModel, IPlatformPollingObjectConnection, PlatformPollingServerTypeEnum } from "./Models";

const CommDataService = {

    getObjectCommData: async function (
        groupID: number,
        objectID: number,
        objectType: eLynxObjectTypeEnum,
        signal?: AbortSignal
    ): Promise<IAcmDeviceProtocolObjectUsage> {

        const path = `/groups/${groupID}/${RouteFromObjectType({ id: objectID, objectType: objectType })}/${objectID}/comm-data`;

        const deviceResponse = await restService.get<IAcmDeviceProtocolObjectUsage>(path, signal);
        return deviceResponse.data;
    },

    getCommData: async function (
        groupID: number,
        signal?: AbortSignal
    ): Promise<Array<IAcmConnectionObject>> {

        const path: string = `groups/${groupID}/comm-data`;

        const response = await restService.get<Array<IAcmConnectionObject>>(path, signal);
        return response.data;
    },

    getProtocolData: async function (
        groupID: number,
        signal?: AbortSignal
    ): Promise<Array<IAcmProtocolObject>> {

        const path: string = `groups/${groupID}/protocol-data`;

        const response = await restService.get<Array<IAcmProtocolObject>>(path, signal);
        return response.data;
    },

    getCommDataUsage: async function (
        groupID: number,
        pollingServer: string,
        commType: AcmObjectTypeEnum,
        commID: number,
        signal?: AbortSignal
    ): Promise<IAcmConnectionObjectUsage> {

        const path: string = `v2/groups/${groupID}/comm-data/${pollingServer}/${commType}/${commID}/usage`;

        const response = await restService.get<IAcmConnectionObjectUsage>(path, signal);
        return response.data;
    },

    getProtocolDataUsage: async function (
        groupID: number,
        pollingServer: string,
        protocolID: number,
        signal?: AbortSignal
    ): Promise<IAcmProtocolObjectUsage> {

        const path: string = `v2/groups/${groupID}/protocol-data/${pollingServer}/${protocolID}/usage`;

        const response = await restService.get<IAcmProtocolObjectUsage>(path, signal);
        return response.data;
    },

    saveDirectCommData: async function (
        groupID: number,
        tcpIpParams: IAcmTcpIpParams,
        signal?: AbortSignal
    ): Promise<IAcmTcpIpConnectionObject> {

        const path: string = `groups/${groupID}/comm-data/direct`;

        if ((tcpIpParams?.id ?? null) !== null && (tcpIpParams?.id ?? 0) > 0) {
            // This is an update
            const response = await restService.put<IAcmTcpIpParams, IAcmTcpIpConnectionObject>(path, tcpIpParams, signal);
            return response.data;
        }
        else {
            // This is a create
            const response = await restService.post<IAcmTcpIpParams, IAcmTcpIpConnectionObject>(path, tcpIpParams, signal);
            return response.data;
        }
    },

    saveSharedCommData: async function (
        groupID: number,
        tcpListenParams: IAcmTcpListenParams,
        signal?: AbortSignal
    ): Promise<IAcmTcpListenConnectionObject> {

        const path: string = `groups/${groupID}/comm-data/shared`;

        if ((tcpListenParams?.id ?? null) !== null && (tcpListenParams.id ?? 0) > 0) {
            // This is an update
            const response = await restService.put<IAcmTcpListenParams, IAcmTcpListenConnectionObject>(path, tcpListenParams, signal);
            return response.data;
        }
        else {
            // This is a create
            const response = await restService.post<IAcmTcpListenParams, IAcmTcpListenConnectionObject>(path, tcpListenParams, signal);
            return response.data;
        }
    },

    getIPAddressData: async function (
        groupID: number,
        signal?: AbortSignal
    ): Promise<Array<ICIDRAddress>> {

        const path: string = `groups/${groupID}/ipaddresses`;

        const response = await restService.get<Array<ICIDRAddress>>(path, signal);
        return response.data;
    },

    saveProtocolObjectData: async function (
        groupID: number,
        protocolParams: IAcmProtocolParams,
        signal?: AbortSignal
    ): Promise<IAcmProtocolObject> {

        const path: string = `groups/${groupID}/protocol-data`;

        const queryParms = {
            groupID: groupID
        };
        const response = await restService.put<IAcmProtocolParams, { groupID: number; }, IAcmProtocolObject>(path, protocolParams, signal, queryParms);
        return response.data;
    },

    deleteCommData: async function (
        groupID: number,
        pollingServer: string,
        commType: AcmObjectTypeEnum,
        commID: number,
        signal?: AbortSignal
    ): Promise<void> {

        const path: string = `v2/groups/${groupID}/comm-data/${pollingServer}/${commType}/${commID}`;

        const queryParms = {
            groupID: groupID,
            commID: commID
        };

        const response = await restService.delete<void, { groupID: number; commID: number; }>(path, signal, queryParms);
        return response.data;
    },

    getCommDataDevices: async function (
        contextGroupID: number,
        signal?: AbortSignal
    ): Promise<Array<ICommDeviceModel>> {

        const path: string = `groups/${contextGroupID}/comm-data/devices`;

        const queryParams = {
            contextGroupID: contextGroupID
        };

        const response = await restService.get<Array<ICommDeviceModel>, { contextGroupID: number; }>(path, signal, queryParams);
        return response.data;
    },

    checkAcmFolderExists: async function (
        pollingServer: string,
        folderID?: number,
        folderName?: string,
        signal?: AbortSignal
    ): Promise<boolean> {

        const path: string = `platform/acm-folder/exists`;

        const folderParams = {
            pollingServer: pollingServer,
            folderID: folderID,
            folderName: folderName
        };

        const response = await restService.post<{ pollingServer: string; folderID?: number; folderName?: string; }, boolean>(path, folderParams, signal);
        return response.data;
    },

    getPlatformPollingConnections: async function (
        customerID?: number,
        serverType?: PlatformPollingServerTypeEnum,
        signal?: AbortSignal
    ): Promise<Array<IPlatformPollingObjectConnection>> {

        const path: string = `platform/polling/connections/query`;

        const connectionParams = {
            customerID: customerID,
            pollingServerType: serverType
        };

        const response = await restService.post<{ customerID?: number; serverType?: PlatformPollingServerTypeEnum; }, Array<IPlatformPollingObjectConnection>>(path, connectionParams, signal);
        return response.data;
    },

    updateTables: async function (
        contextGroupID: number,
        pollingServer: string | undefined,
        folderID: number | undefined,
        folderName: string | undefined,
        forceRebuild: boolean,
        signal?: AbortSignal
    ): Promise<boolean> {

        const path: string = `groups/${contextGroupID}/platform/objects/rebuild`;

        const buildParams = {
            pollingServer: pollingServer,
            folderID: folderID,
            folderName: folderName,
            forceRebuild: forceRebuild
        };

        const response = await restService.post<{
            pollingServer: string | undefined;
            folderID: number | undefined;
            folderName: string | undefined;
            forceRebuild: boolean;
        }, boolean>(path, buildParams, signal);
        return response.data;
    }
};

export default CommDataService;